import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from "@/lib/constants";

/** Access JWT lifetime (matches server default jwt_expiration_hours). */
const ACCESS_MAX_AGE_SEC = 24 * 60 * 60;
/** Refresh JWT lifetime (matches server default jwt_refresh_expiration_hours). */
const REFRESH_MAX_AGE_SEC = 7 * 24 * 60 * 60;

function cookieAttrs(maxAgeSeconds: number): string {
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "; Secure"
      : "";
  return `; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split("; ")) {
    if (part.startsWith(prefix)) {
      try {
        return decodeURIComponent(part.slice(prefix.length));
      } catch {
        return part.slice(prefix.length);
      }
    }
  }
  return null;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}${cookieAttrs(maxAgeSeconds)}`;
}

function deleteCookie(name: string) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${cookieAttrs(0)}`;
}

function migrateLegacyLocalStorage(): void {
  if (typeof window === "undefined") return;
  const access = localStorage.getItem(ACCESS_TOKEN_KEY);
  const refresh = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!access && !refresh) return;
  if (access) writeCookie(ACCESS_TOKEN_KEY, access, ACCESS_MAX_AGE_SEC);
  if (refresh) writeCookie(REFRESH_TOKEN_KEY, refresh, REFRESH_MAX_AGE_SEC);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function getAccessToken(): string | null {
  if (typeof window === "undefined") return null;
  const fromCookie = readCookie(ACCESS_TOKEN_KEY);
  if (fromCookie) return fromCookie;
  migrateLegacyLocalStorage();
  return readCookie(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  if (typeof window === "undefined") return null;
  const fromCookie = readCookie(REFRESH_TOKEN_KEY);
  if (fromCookie) return fromCookie;
  migrateLegacyLocalStorage();
  return readCookie(REFRESH_TOKEN_KEY);
}

export function setTokens(accessToken: string, refreshToken?: string) {
  writeCookie(ACCESS_TOKEN_KEY, accessToken, ACCESS_MAX_AGE_SEC);
  if (refreshToken) {
    writeCookie(REFRESH_TOKEN_KEY, refreshToken, REFRESH_MAX_AGE_SEC);
  }
  // Drop any leftover localStorage tokens from older builds.
  if (typeof window !== "undefined") {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
}

export function clearTokens() {
  deleteCookie(ACCESS_TOKEN_KEY);
  deleteCookie(REFRESH_TOKEN_KEY);
  if (typeof window !== "undefined") {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
}
